/* ════ HR NOTES: ENCRYPTION ════════════════════════════════ */
// Notes on absence records (e.g. the reason for sickness) are encrypted in the
// browser before they're saved, with a key derived from a passphrase only the
// approver and HR viewers know. The database only ever holds ciphertext, so
// nobody with database access can read them without the passphrase.
//
// AES-GCM 256, key from PBKDF2-SHA-256 (310,000 rounds) with a random salt.
// Passphrases are versioned (migration 0031, table hr_keys): each holds a salt
// and a check value (an encrypted known string, to tell a right passphrase from
// a wrong one), never the passphrase itself. The newest unretired key is the
// current one; each note records which key locked it (absence_records.key_id).
// A forgotten passphrase can be reset: the old key is retired, its notes stay
// locked (not deleted) and open again if the old passphrase is ever entered.
import { state } from './state.js';
import { sb } from './supabase/client.js';

const enc = new TextEncoder(), dec = new TextDecoder();
const CHECK = 'grantt-hr-ok';
const SESSION_KEY = 'grantt_hr_keys';
const unlocked = new Map();   // key id → CryptoKey (this tab only)

const b64 = buf => btoa(String.fromCharCode(...new Uint8Array(buf)));
const unb64 = s => Uint8Array.from(atob(s), c => c.charCodeAt(0));

async function derive(pass, saltB64) {
  const base = await crypto.subtle.importKey('raw', enc.encode(pass), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey({ name: 'PBKDF2', salt: unb64(saltB64), iterations: 310000, hash: 'SHA-256' },
    base, { name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}
async function seal(k, text) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, k, enc.encode(text));
  return { c: b64(ct), iv: b64(iv) };
}
async function open(k, c, iv) {
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: unb64(iv) }, k, unb64(c));
  return dec.decode(pt);
}

const keys = () => state.hrKeys || [];
export const currentKey = () => keys().filter(k => !k.retired_at).sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))[0] || null;
export const hrConfigured = () => !!currentKey();
export const hrUnlocked = () => { const k = currentKey(); return !!k && unlocked.has(k.id); };
// Notes still locked because their (older) passphrase hasn't been entered in this tab.
export const lockedNoteCount = (list = state.absences || []) => list.filter(a => a.note_enc && !unlocked.has(a.key_id)).length;
// Notes locked with a passphrase other than the current one.
export const oldKeyNoteCount = (list = state.absences || []) => { const k = currentKey(); return list.filter(a => a.note_enc && a.key_id !== k?.id).length; };

async function newKey(pass) {
  const salt = b64(crypto.getRandomValues(new Uint8Array(16)));
  const k = await derive(pass.trim(), salt);
  return { k, salt, check: JSON.stringify(await seal(k, CHECK)) };
}

// First-time setup by the approver. Never replaces an existing passphrase: the
// database allows only one current key, so a second setup (e.g. from another
// tab) fails instead of stranding notes.
export async function hrSetup(pass) {
  const { k, salt, check } = await newKey(pass);
  const { data, error } = await sb.from('hr_keys').insert({ salt, check_value: check, created_by: state.currentUser?.email || null }).select().single();
  if (error) {
    if (/duplicate|conflict|23505|unique/i.test(`${error.code} ${error.message}`)) throw new Error('A passphrase has already been set. Refresh the page and unlock with it.');
    throw error;
  }
  state.hrKeys = [...keys(), data];
  await remember(data.id, k);
}

// Reset a forgotten passphrase (approver only): retire the current key, start a
// new one. Notes locked with the old one are kept and stay locked.
export async function hrReset(pass) {
  const { k, salt, check } = await newKey(pass);
  const { data: id, error } = await sb.rpc('hr_rotate_key', { p_salt: salt, p_check: check });
  if (error) throw error;
  const now = new Date().toISOString();
  state.hrKeys = [...keys().map(x => (x.retired_at ? x : { ...x, retired_at: now })),
    { id, salt, check_value: check, created_at: now, retired_at: null }];
  await remember(id, k);
}

// Try a passphrase against every key (current and retired). Forgiving about
// invisible slips: spaces at either end, and curly quotes some keyboards and
// documents substitute for straight ones. Returns which keys it opened.
const straight = s => s.replace(/[‘’‚‛]/g, "'").replace(/[“”„‟]/g, '"');
export async function hrUnlock(pass) {
  const tries = [...new Set([pass, pass.trim(), straight(pass), straight(pass.trim())])].filter(Boolean);
  const cur = currentKey(), opened = { current: false, older: 0 };
  for (const key of keys()) {
    if (unlocked.has(key.id)) continue;
    let check; try { check = JSON.parse(key.check_value); } catch (e) { continue; }
    for (const p of tries) {
      try {
        const k = await derive(p, key.salt);
        if (await open(k, check.c, check.iv) === CHECK) {
          await remember(key.id, k);
          if (key.id === cur?.id) opened.current = true; else opened.older++;
          break;
        }
      } catch (e) { /* wrong passphrase for this key: try the next variant */ }
    }
  }
  return opened;
}

// Keep unlocked keys for this browser tab only (cleared when the tab closes).
async function remember(id, k) {
  unlocked.set(id, k);
  try {
    const all = JSON.parse(sessionStorage.getItem(SESSION_KEY) || '{}');
    all[id] = b64(await crypto.subtle.exportKey('raw', k));
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(all));
  } catch (e) { /* private mode */ }
}
export function hrLock() { unlocked.clear(); try { sessionStorage.removeItem(SESSION_KEY); } catch (e) { /* fine */ } }
export async function hrRestore() {
  try {
    const all = JSON.parse(sessionStorage.getItem(SESSION_KEY) || '{}');
    for (const [id, raw] of Object.entries(all)) {
      if (!unlocked.has(id)) unlocked.set(id, await crypto.subtle.importKey('raw', unb64(raw), 'AES-GCM', true, ['encrypt', 'decrypt']));
    }
  } catch (e) { /* nothing saved */ }
  return hrUnlocked();
}

// New notes are always locked with the current passphrase.
export async function encryptNote(text) {
  if (!text) return { note_enc: null, note_iv: null, key_id: null };
  const cur = currentKey();
  if (!cur || !unlocked.has(cur.id)) throw new Error('Unlock HR notes first');
  const { c, iv } = await seal(unlocked.get(cur.id), text);
  return { note_enc: c, note_iv: iv, key_id: cur.id };
}
export async function decryptNote(rec) {
  const k = rec?.note_enc && unlocked.get(rec.key_id);
  if (!k) return null;
  try { return await open(k, rec.note_enc, rec.note_iv); } catch (e) { return null; }
}
